import { ArrowLeft, Settings } from 'lucide-react';
import { type QueueResponse } from '@/api/client';
import { QueuePublicAccessPanel } from '@/components/QueuePublicAccessPanel';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';

interface QueueSettingsDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	queue: QueueResponse | null;
	onBack?: () => void;
}

/**
 * QueueSettingsDialog - Modal for managing queue settings
 *
 * Features:
 * - Public access management (QueuePublicAccessPanel)
 * - Queue configuration options
 * - Owner-only access
 */
export function QueueSettingsDialog({ open, onOpenChange, queue, onBack }: QueueSettingsDialogProps) {
	if (!queue || queue.id === 0) return null; // Don't show for "incoming" virtual queue

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-2xl max-h-[90vh] p-0 gap-0 overflow-hidden">
				{/* Header */}
				<DialogHeader className="px-6 pt-6 pb-4 border-b">
					<div className="flex items-center gap-2">
						{onBack && (
							<Button
								variant="ghost"
								size="icon"
								onClick={onBack}
								className="size-8 shrink-0"
							>
								<ArrowLeft className="size-4" />
							</Button>
						)}
						<div
							className="size-4 rounded-full shrink-0"
							style={{ backgroundColor: queue.color }}
						/>
						<DialogTitle className="text-xl">
							{queue.icon && <span className="mr-2">{queue.icon}</span>}
							{queue.name} Settings
						</DialogTitle>
					</div>
					<DialogDescription>
						Manage settings and public access for this queue
					</DialogDescription>
				</DialogHeader>

				{/* Content */}
				<ScrollArea className="flex-1 max-h-[calc(90vh-8rem)]">
					<div className="px-6 py-4 space-y-6">
						{/* Public Access Section */}
						<div>
							<h3 className="text-sm font-medium mb-3 flex items-center gap-2">
								<Settings className="size-4" />
								Public Access
							</h3>
							<QueuePublicAccessPanel queueId={queue.id} />
						</div>

						{/* Future sections can go here */}
						{/* Queue Info, Permissions, etc. */}
					</div>
				</ScrollArea>
			</DialogContent>
		</Dialog>
	);
}
